import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  CheckCircle2, MapPin, Phone, Mail, Globe, MessageCircle, Building2,
  Briefcase, Languages, Instagram, Facebook, Linkedin, Calendar,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PropertyCard from '../components/PropertyCard';
import SEO from '../components/SEO';
import VideoEmbedCard from '../components/profile/VideoEmbedCard';
import GalleryGrid from '../components/profile/GalleryGrid';
import { HistoryCard, StatsSoonCard } from '../components/profile/ProfileCards';
import { api, friendlyError } from '../lib/api';
import { track, EventTypes } from '../lib/analytics';

const TABS = [
  { id: 'about', label: 'Acerca de' },
  { id: 'properties', label: 'Propiedades' },
];

export default function AgentProfile() {
  const { slug } = useParams();
  const [agent, setAgent] = useState(null);
  const [props, setProps] = useState([]);
  const [tab, setTab] = useState('about');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.getAgent(slug),
      api.getAgentProperties(slug, { limit: 24 }),
    ])
      .then(([a, p]) => {
        setAgent(a);
        setProps(p?.items || []);
        if (a?.id) track(EventTypes.AGENT_PROFILE_VIEWED, { agent_id: a.id });
      })
      .catch((e) => setErr(friendlyError(e, 'No pudimos cargar el asesor.')))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-habita border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (err || !agent) {
    return (
      <>
        <Header />
        <div className="max-w-xl mx-auto p-12 text-center" data-testid="agent-not-found">
          <p>{err || 'No encontrado'}</p>
        </div>
        <Footer />
      </>
    );
  }

  const cover = agent.cover_url || 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600&h=500&fit=crop';
  const whatsappLink = agent.whatsapp
    ? `https://wa.me/${String(agent.whatsapp).replace(/\D/g, '')}?text=${encodeURIComponent(`Hola ${agent.name}, vi tu perfil en habita.co y me interesa hablar contigo.`)}`
    : null;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'RealEstateAgent',
    name: agent.name,
    image: agent.avatar,
    address: { '@type': 'PostalAddress', addressLocality: agent.city, addressCountry: 'CO' },
    telephone: agent.phone,
    email: agent.email,
    description: agent.bio || agent.history,
  };

  return (
    <div className="min-h-screen bg-habita-cream">
      <SEO
        title={`${agent.name}${agent.title ? ' · ' + agent.title : ''} · Asesor inmobiliario en ${agent.city || 'Colombia'}`}
        description={agent.bio || `${agent.name} es asesor inmobiliario en ${agent.city || 'Colombia'} con ${agent.active_properties} propiedades activas en habita.co.`}
        image={agent.avatar || cover}
        jsonLd={jsonLd}
      />
      <Header />

      {/* HERO */}
      <div className="relative" data-testid="agent-hero">
        <div className="h-44 md:h-60 lg:h-72 overflow-hidden bg-habita-dark">
          <img src={cover} alt="" className="w-full h-full object-cover opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        </div>
        <div className="max-w-6xl mx-auto px-4 lg:px-8 -mt-16 lg:-mt-20 relative">
          <div className="flex flex-col md:flex-row md:items-end gap-5">
            <div className="w-28 h-28 lg:w-36 lg:h-36 rounded-full bg-white border-4 border-white shadow-xl overflow-hidden shrink-0">
              {agent.avatar
                ? <img src={agent.avatar} alt={agent.name} className="w-full h-full object-cover" />
                : <div className="w-full h-full bg-habita-mint flex items-center justify-center text-habita text-3xl font-bold">{agent.name.charAt(0)}</div>}
            </div>
            <div className="flex-1 min-w-0 pb-2">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-serif text-2xl lg:text-4xl font-bold text-habita-dark break-words">{agent.name}</h1>
                {agent.verified && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold" data-testid="agent-verified-badge">
                    <CheckCircle2 className="w-3.5 h-3.5" /> AGENTE VERIFICADO
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-600 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                {agent.title && <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> {agent.title}</span>}
                {agent.city && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {agent.city}</span>}
                {agent.years_experience != null && (
                  <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {agent.years_experience} años exp.</span>
                )}
                <span>· {agent.active_properties} propiedades activas</span>
              </div>
              {agent.agency && (
                <Link
                  to={`/inmobiliaria/${agent.agency.slug}`}
                  onClick={() => track(EventTypes.AGENT_AGENCY_CLICKED, { agent_id: agent.id, agency_id: agent.agency.id, source: 'profile_hero_link' })}
                  className="mt-2 inline-flex items-center gap-2 text-xs font-semibold text-habita hover:underline"
                  data-testid="agent-agency-link"
                >
                  {agent.agency.logo_url
                    ? <img src={agent.agency.logo_url} alt="" className="w-5 h-5 rounded object-cover" />
                    : <Building2 className="w-3.5 h-3.5" />}
                  Miembro de {agent.agency.name}
                  {agent.agency.verified && <CheckCircle2 className="w-3 h-3 text-emerald-600" />}
                </Link>
              )}
            </div>
            <div className="flex flex-wrap gap-2 pb-2 shrink-0">
              {whatsappLink && (
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="agent-whatsapp-btn"
                  onClick={() => track(EventTypes.AGENT_WHATSAPP_CLICKED, { agent_id: agent.id })}
                  className="btn-habita inline-flex items-center gap-1.5 px-4 h-10 rounded-full text-sm font-semibold"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
              )}
              {agent.phone && (
                <a
                  href={`tel:${agent.phone}`}
                  className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full text-sm font-semibold border border-gray-300 hover:border-habita bg-white"
                  data-testid="agent-call-btn"
                  onClick={() => track(EventTypes.AGENT_PHONE_CLICKED, { agent_id: agent.id })}
                >
                  <Phone className="w-4 h-4" /> Llamar
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* STATS STRIP — prominent counters below hero */}
      <div className="max-w-6xl mx-auto px-4 lg:px-8 mt-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="agent-stats-strip">
          <div className="bg-white rounded-2xl border border-gray-200 p-4">
            <p className="font-serif text-2xl font-bold text-habita-dark tabular-nums">{agent.active_properties}</p>
            <p className="text-[11px] uppercase tracking-wider text-gray-500 mt-1">Propiedades activas</p>
          </div>
          {agent.sales > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <p className="font-serif text-2xl font-bold text-habita-dark tabular-nums">{agent.sales}</p>
              <p className="text-[11px] uppercase tracking-wider text-gray-500 mt-1">Vendidas / cerradas</p>
            </div>
          )}
          <div className="bg-white rounded-2xl border border-gray-200 p-4">
            <p className="font-serif text-lg font-bold text-habita-dark truncate">{agent.city || 'Colombia'}</p>
            <p className="text-[11px] uppercase tracking-wider text-gray-500 mt-1">Ciudad principal</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-200 p-4">
            <p className="font-serif text-lg font-bold text-habita-dark truncate">
              {agent.specialties?.length ? agent.specialties.slice(0, 2).join(' · ') : 'Asesor general'}
            </p>
            <p className="text-[11px] uppercase tracking-wider text-gray-500 mt-1">
              {agent.specialties?.length ? `${agent.specialties.length} especialidad${agent.specialties.length === 1 ? '' : 'es'}` : 'Especialidades'}
            </p>
          </div>
        </div>
      </div>

      {/* TABS */}
      <div className="max-w-6xl mx-auto px-4 lg:px-8 mt-8 sticky top-16 bg-habita-cream z-20 border-b border-gray-200">
        <div className="flex gap-1">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`agent-tab-${t.id}`}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${tab === t.id ? 'border-habita text-habita' : 'border-transparent text-gray-600 hover:text-habita-dark'}`}>
              {t.label}{t.id === 'properties' ? ` (${agent.active_properties})` : ''}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8 grid lg:grid-cols-3 gap-8">
        <main className="lg:col-span-2 space-y-6">
          {tab === 'about' && (
            <>
              <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="agent-about-card">
                <h2 className="font-serif text-xl font-bold text-habita-dark">Sobre mí</h2>
                <p className="text-sm text-gray-700 mt-3 whitespace-pre-line leading-relaxed">
                  {agent.bio || `${agent.name} es asesor inmobiliario en ${agent.city || 'Colombia'}.`}
                </p>
                {agent.specialties?.length > 0 && (
                  <div className="mt-5">
                    <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Especialidades</p>
                    <div className="flex flex-wrap gap-2" data-testid="agent-specialties">
                      {agent.specialties.map(s => (<span key={s} className="px-3 py-1.5 rounded-full bg-habita-mint text-habita text-xs font-semibold">{s}</span>))}
                    </div>
                  </div>
                )}
                {agent.cities_operating?.length > 0 && (
                  <div className="mt-5">
                    <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Ciudades donde trabajo</p>
                    <div className="flex flex-wrap gap-2" data-testid="agent-cities-operating">
                      {agent.cities_operating.map(c => (
                        <span key={c} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-habita-cream border border-habita/20 text-habita-dark text-xs font-semibold">
                          <MapPin className="w-3 h-3 text-habita" /> {c}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {agent.languages?.length > 0 && (
                  <div className="mt-5">
                    <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Idiomas</p>
                    <div className="flex flex-wrap gap-2" data-testid="agent-languages">
                      {agent.languages.map(l => (
                        <span key={l} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-habita-cream border border-habita/20 text-habita-dark text-xs font-semibold">
                          <Languages className="w-3 h-3 text-habita" /> {l}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <HistoryCard text={agent.history} title="Mi trayectoria" testid="agent-history-card" />
              <VideoEmbedCard url={agent.video_url} title={`${agent.name} · Video de presentación`} testid="agent-video-card" />
              <GalleryGrid images={agent.gallery} title="Galería" testid="agent-gallery" altPrefix={`${agent.name} galería`} />

              {agent.active_properties > 0 && (
                <div className="bg-habita text-white rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="agent-see-all-cta">
                  <div>
                    <p className="font-serif text-lg font-bold">{agent.active_properties} propiedades activas</p>
                    <p className="text-sm opacity-90">Conoce el portafolio completo de {agent.name?.split(' ')[0]}</p>
                  </div>
                  <button
                    onClick={() => setTab('properties')}
                    className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-white text-habita font-semibold text-sm hover:bg-habita-cream transition-colors"
                    data-testid="agent-see-all-btn"
                  >
                    Ver todas las propiedades →
                  </button>
                </div>
              )}

              <StatsSoonCard
                labels={['Vistas perfil', 'Contactos WhatsApp', 'Mensajes', 'Visitas agendadas']}
                title="Estadísticas"
                testid="agent-stats-soon"
              />
            </>
          )}

          {tab === 'properties' && (
            props.length === 0
              ? <div className="bg-white rounded-2xl p-12 text-center text-gray-500" data-testid="agent-no-properties">Este asesor aún no tiene propiedades publicadas.</div>
              : <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{props.map(p => <PropertyCard key={p.id} p={p} />)}</div>
          )}
        </main>

        <aside className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="agent-contact-card">
            <h3 className="font-serif text-base font-bold text-habita-dark mb-3">Contacto</h3>
            <div className="space-y-2.5 text-sm">
              {agent.phone && (
                <div className="flex items-center gap-2 text-gray-700">
                  <Phone className="w-4 h-4 text-habita" />
                  <a href={`tel:${agent.phone}`}>{agent.phone}</a>
                </div>
              )}
              {agent.email && (
                <div className="flex items-center gap-2 text-gray-700">
                  <Mail className="w-4 h-4 text-habita" />
                  <a href={`mailto:${agent.email}`} className="break-all">{agent.email}</a>
                </div>
              )}
              {whatsappLink && (
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="btn-habita w-full inline-flex items-center justify-center gap-1.5 px-4 h-10 rounded-full text-sm font-semibold mt-2">
                  <MessageCircle className="w-4 h-4" /> Escribir por WhatsApp
                </a>
              )}
            </div>
          </div>

          {agent.social && Object.keys(agent.social).filter(k => agent.social[k]).length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="agent-social-card">
              <h3 className="font-serif text-base font-bold text-habita-dark mb-3">Sígueme</h3>
              <div className="flex gap-2">
                {agent.social.instagram && <a href={agent.social.instagram} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-habita-mint flex items-center justify-center text-habita hover:bg-habita hover:text-white transition-colors"><Instagram className="w-4 h-4" /></a>}
                {agent.social.facebook && <a href={agent.social.facebook} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-habita-mint flex items-center justify-center text-habita hover:bg-habita hover:text-white transition-colors"><Facebook className="w-4 h-4" /></a>}
                {agent.social.linkedin && <a href={agent.social.linkedin} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-habita-mint flex items-center justify-center text-habita hover:bg-habita hover:text-white transition-colors"><Linkedin className="w-4 h-4" /></a>}
                {agent.social.tiktok && <a href={agent.social.tiktok} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-habita-mint flex items-center justify-center text-habita hover:bg-habita hover:text-white transition-colors text-xs font-bold"><Globe className="w-4 h-4" /></a>}
              </div>
            </div>
          )}

          {agent.agency && (
            <Link to={`/inmobiliaria/${agent.agency.slug}`} onClick={() => track(EventTypes.AGENT_AGENCY_CLICKED, { agent_id: agent.id, agency_id: agent.agency.id, source: 'profile_sidebar_card' })} className="block bg-white rounded-2xl border border-gray-200 p-5 hover:border-habita transition-colors" data-testid="agent-agency-card">
              <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Trabaja en</p>
              <div className="flex items-center gap-3">
                {agent.agency.logo_url
                  ? <img src={agent.agency.logo_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
                  : <div className="w-12 h-12 rounded-lg bg-habita-mint flex items-center justify-center text-habita font-bold">{agent.agency.name?.charAt(0)}</div>}
                <div className="min-w-0">
                  <p className="font-semibold text-habita-dark truncate">{agent.agency.name}</p>
                  {agent.agency.city && <p className="text-xs text-gray-500">{agent.agency.city}</p>}
                </div>
              </div>
            </Link>
          )}
        </aside>
      </div>

      <Footer />
    </div>
  );
}
